import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSnack } from 'hooks';

// KD-3532 — an API error carries its own message in the response body, and
// dropping it turned every backend failure into the same "An error occurred",
// which told the user nothing about what to do next. Surface that message when
// it reads like a sentence meant for a human; anything else (a JSON blob, a
// serialized stack, a bare code) still falls back to the generic wording.
const MAX_BACKEND_MESSAGE_LENGTH = 200;

const getBackendMessage = (err) => {
  const data = err?.response?.data;
  const message = typeof data === 'string' ? data : data?.message;

  if (typeof message !== 'string') {
    return undefined;
  }

  const trimmed = message.trim();
  const isReadable =
    Boolean(trimmed) &&
    trimmed.length <= MAX_BACKEND_MESSAGE_LENGTH &&
    !trimmed.includes('\n') &&
    !/^[[{"]/.test(trimmed);

  return isReadable ? trimmed : undefined;
};

const useError = () => {
  const { t } = useTranslation('errors');
  const { notify } = useSnack();

  const formatError = ({ attribute, message }) =>
    Boolean(attribute)
      ? t(`${camelCase(`${attribute}_${message}`)}`)
      : t('error');

  const handleSendErrorSnack = (err) =>
    notify(getBackendMessage(err) ?? formatError(err), { type: 'error' });

  return {
    formatError,
    sendErrorSnack: handleSendErrorSnack,
  };
};

export default useError;
